import CalcInput from "@/components/calculators/CalcInput"
import ProfitabilityIndicator from "@/components/calculators/ProfitabilityIndicator"
import ResultCard from "@/components/calculators/ResultCard"
import SEOHead from "@/components/SEOHead"
import { formatCurrency, formatCurrencyRounded, formatPercent, formatRatio, safeDivide, toNumber } from "@/lib/format"
import {Copy, RotateCcw} from 'lucide-react'
import { useState } from "react"
import toast from "react-hot-toast"

function Home() {
  const [spend, setSpend] = useState("1000")
  const [revenue, setRevenue] = useState("4000")

  const spendNum = toNumber(spend)
  const revenueNum = toNumber(revenue)

  const roas = safeDivide(revenueNum, spendNum)
  const roasPercent = roas * 100
  const profit = revenueNum - spendNum
  const roi = safeDivide(profit, spendNum) * 100
  const costPerDollar = safeDivide(spendNum, revenueNum)

  const handleReset = () => {
    setSpend("1000")
    setRevenue("4000")
  }

  const handleCopy = () => {
    const text = `Ads ROAS Results: Spend: ${formatCurrencyRounded(spendNum)} | Revenue: ${formatCurrencyRounded(revenueNum)} | ROAS: ${formatRatio(roas)} (${formatPercent(roasPercent)}) | Net Profit: ${formatCurrencyRounded(profit)}. Calculate yours at adsroascalculator.com`
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Results copied to clipboard!"))
      .catch(() => toast.error("Could not copy results."))
  }

  return (
    <div>
      <SEOHead
        title="Professional Ads ROAS Calculator"
        description="Calculate Return on Ad Spend, net profit, and advertising ROI instantly. Free tool for data-driven decisions on PPC and paid social campaigns."
      />

      <section className="border-b border-slate-200 bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_58%)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-12 sm:pb-16">
          <div className="max-w-2xl mb-8 sm:mb-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 mb-3">Free PPC utility</p>
            <h1 className="text-[1.85rem] sm:text-[2.5rem] font-extrabold text-slate-900 tracking-tight leading-[1.15]">
              Professional Ads ROAS Calculator
            </h1>
            <p className="mt-3 sm:mt-4 text-[15px] sm:text-base text-slate-600 leading-relaxed">
              Calculate Return on Ad Spend, net profits, and advertising ROI instantly. Run data-driven decisions on your PPC campaigns.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-4 sm:gap-5">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Campaign inputs</p>
              <div className="space-y-5">
                <CalcInput
                  label="Ad Spend"
                  prefix="$"
                  value={spend}
                  onChange={setSpend}
                  placeholder="1000"
                  tooltip="Total amount spent on advertising for the campaign or time period you're analyzing."
                />
                <CalcInput
                  label="Total Revenue Generated"
                  prefix="$"
                  value={revenue}
                  onChange={setRevenue}
                  placeholder="4000"
                  tooltip="Total revenue directly attributed to the ads that generated the spend above."
                />
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-[13px] px-4 py-2.5 rounded-lg shadow-sm shadow-emerald-600/20 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy Results
                </button>
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-[13px] px-4 py-2.5 rounded-lg border border-slate-200 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Live results</p>
              <ProfitabilityIndicator roas={roas} />
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <ResultCard label="ROAS" value={formatRatio(roas)} emphasis formula="Revenue / Ad Spend" />
                <ResultCard label="ROAS %" value={formatPercent(roasPercent)} formula="(Revenue / Ad Spend) x 100" />
                <ResultCard label="Total Profit" value={formatCurrencyRounded(profit)} formula="Revenue - Ad Spend" />
                <ResultCard label="ROI" value={`${roi.toFixed(2)}%`} formula="((Revenue - Ad Spend) / Ad Spend) x 100" />
                <ResultCard label="Cost per $ Earned" value={formatCurrency(costPerDollar)} formula="Ad Spend / Revenue" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <h2 className="text-2xl sm:text-[1.7rem] font-extrabold text-slate-900 tracking-tight mb-4">What is ROAS & How is it Calculated?</h2>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-4">
          Return on Ad Spend (ROAS) is a key marketing metric that measures the efficacy of digital advertising.
          The calculation is simple: divide your total campaign revenue by your total advertising cost. Our tool
          handles this instantly to help you keep track of campaign health.
        </p>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-10">
          Unlike broader profitability metrics, ROAS focuses purely on the relationship between ad spend and the
          revenue it generates — making it the fastest way to gauge whether a specific campaign, ad set, or
          keyword is pulling its weight.
        </p>

        <h3 className="text-lg font-extrabold text-slate-900 tracking-tight mb-3">Why Measuring ROAS Matters for PPC Campaigns</h3>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-4">
          Platforms like Google Ads and Meta Ads increasingly rely on automated, machine-learning-driven bidding
          strategies such as Target ROAS. Feeding these systems accurate ROAS data helps the algorithm optimize
          bids toward your most valuable customers, rather than simply chasing clicks or impressions.
        </p>
        <p className="text-[15px] text-slate-600 leading-relaxed">
          Tracking ROAS consistently also helps you identify underperforming campaigns before they drain your
          budget, reallocate spend toward high-return channels, and set realistic growth targets grounded in real
          unit economics rather than vanity metrics.
        </p>
      </section>
    </div>
  )
}

export default Home
